import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { Leaf } from "lucide-react";

const PERIODS = [
  { value: "month", key: "vco2.period.month" },
  { value: "quarter", key: "vco2.period.quarter" },
  { value: "year", key: "vco2.period.year" },
];

export default function Co2Reports() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [periodType, setPeriodType] = useState("month");
  const [projects, setProjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const params = { period_type: periodType };
    if (projectFilter) params.project_id = projectFilter;
    if (vehicleFilter) params.vehicle_id = vehicleFilter;
    api.get("/vehicle-co2/summary", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [user, periodType, projectFilter, vehicleFilter]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const fmt = (n) => Math.round(n).toLocaleString();
  const max = Math.max(...(data?.series || []).map((d) => d.co2_kg), 1);

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="mb-6">
          <div className="label-tech">{t("veh.label")}</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("vco2.title")}</h1>
          <div className="text-[var(--ink-700)] mt-2 text-sm">{t("vco2.subtitle")}</div>
        </div>

        <VehiclesSubNav />

        <div className="flex flex-wrap gap-3 mb-6">
          <select value={periodType} onChange={(e) => setPeriodType(e.target.value)} className="input-tech max-w-[180px]" data-testid="vco2-period-select">
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{t(p.key)}</option>)}
          </select>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="input-tech max-w-[220px]">
            <option value="">{t("vco2.allProjects")}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
          </select>
          <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)} className="input-tech max-w-[200px]">
            <option value="">{t("vco2.allVehicles")}</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.regnr}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vco2-total-card">
            <div className="label-tech inline-flex items-center gap-2"><Leaf size={12} /> CO₂e</div>
            <div className="font-display text-3xl tracking-tighter mt-2">{fmt(data?.total_co2_kg || 0)} <span className="text-base text-[var(--ink-500)]">{t("vco2.kgCO2")}</span></div>
          </div>
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 md:col-span-2" data-testid="vco2-trend-card">
            <div className="label-tech mb-2">{t("vco2.trend")}</div>
            {data?.series?.length ? (
              <div className="flex items-end gap-1 h-28">
                {data.series.map((d) => (
                  <div key={d.period} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-[var(--moss)]" style={{ height: `${Math.max(2, (d.co2_kg / max) * 100)}px` }} title={`${d.period}: ${fmt(d.co2_kg)} kg`} />
                    <div className="text-[9px] font-mono-tech text-[var(--ink-500)] truncate w-full text-center">{d.period.slice(-2)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5">
            <div className="label-tech mb-3">{t("vco2.perProject")}</div>
            {data?.per_project?.length ? (
              <div className="space-y-2">
                {data.per_project.map((p, i) => (
                  <div key={p.project_id || `none-${i}`} className="flex items-center justify-between text-sm">
                    <span>{p.project_name || <span className="italic text-[var(--ink-500)]">—</span>}</span>
                    <span className="font-mono-tech text-[var(--moss)]">{fmt(p.co2_kg)} kg</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>}
          </div>
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5">
            <div className="label-tech mb-3">{t("vco2.topVehicles")}</div>
            {data?.top_vehicles?.length ? (
              <div className="space-y-2">
                {data.top_vehicles.map((v) => (
                  <div key={v.vehicle_id} className="flex items-center justify-between text-sm">
                    <span className="font-mono-tech">{v.regnr || v.vehicle_id.slice(0, 8)}</span>
                    <span className="text-[var(--ink-700)]">{fmt(v.co2_kg)} kg · {fmt(v.distance_km)} km</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
